import { useCallback, useEffect, useMemo, useState } from "react";
import { fetchCompare } from "../lib/api";
import MaslowSphere from "./MaslowSphere";
import { Columns2, Layers } from "lucide-react";

const label = (s) => (s.name && s.name !== "—" ? s.name : s.subject.slice(0, 10));

const Picker = ({ testid, subjects, value, onChange, exclude }) => (
  <select
    data-testid={testid}
    value={value || ""}
    onChange={(e) => onChange(e.target.value)}
    className="border border-[var(--line-hi)] bg-[#0a0a0d] px-3 py-2 font-data text-[11px] text-zinc-100 outline-none"
  >
    <option value="">pick a subject</option>
    {subjects
      .filter((s) => s.subject !== exclude)
      .map((s) => (
        <option key={s.subject} value={s.subject}>
          {`${label(s)} · C ${s.predictive_confidence}% · ${s.coverage.filled}/${s.coverage.total ?? 46}`}
        </option>
      ))}
  </select>
);

/** Two engagements side by side, or merged into one sphere with the differences coded. */
export const EngagementCompare = ({ subjects = [] }) => {
  const [a, setA] = useState(subjects[0]?.subject || "");
  const [b, setB] = useState(subjects[1]?.subject || "");
  const [mode, setMode] = useState("side");
  const [data, setData] = useState(null);
  const [err, setErr] = useState("");

  const load = useCallback(async () => {
    if (!a || !b || a === b) {
      setData(null);
      return;
    }
    setErr("");
    try {
      setData(await fetchCompare(a, b));
    } catch (e) {
      setData(null);
      setErr(e?.response?.data?.detail || "could not compare");
    }
  }, [a, b]);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    if (!subjects.length) return;
    setA((v) => v || subjects[0].subject);
    setB((v) => v || subjects[1]?.subject || "");
  }, [subjects]);

  const merged = useMemo(() => {
    if (!data) return null;
    return {
      tiers: data.a.map.tiers,
      nodes: data.diff.nodes.map((n) => {
        const sal = Math.max(n.a_salience, n.b_salience);
        return {
          ...n,
          salience: sal,
          radius: 0.06 + 0.1 * (sal / 100),
          tier_name: "",
          business_lens: "",
          fill_confidence: sal,
          evidence: [],
        };
      }),
      influence_nodes: data.diff.edges,
      candidate_edges: [],
    };
  }, [data]);

  const compareSides = useMemo(
    () =>
      data ? Object.fromEntries(data.diff.nodes.map((n) => [n.id, n.side])) : null,
    [data]
  );

  const sum = data?.diff?.summary;

  return (
    <div data-testid="admin-engagement-compare" className="space-y-6">
      <div className="flex flex-wrap items-center gap-3">
        <span className="label">A</span>
        <Picker testid="compare-subject-a" subjects={subjects} value={a} onChange={setA} exclude={b} />
        <span className="label">B</span>
        <Picker testid="compare-subject-b" subjects={subjects} value={b} onChange={setB} exclude={a} />
        <div className="ml-auto flex items-center gap-1">
          {[
            ["side", "Side by side", Columns2],
            ["merged", "Merged", Layers],
          ].map(([key, text, Icon]) => (
            <button
              key={key}
              data-testid={`compare-view-${key}`}
              onClick={() => setMode(key)}
              className={`flex items-center gap-2 px-3 py-2 font-data text-[10px] uppercase tracking-[0.14em] transition-colors ${
                mode === key ? "bg-zinc-100 text-[var(--void)]" : "text-zinc-500 hover:text-zinc-200"
              }`}
            >
              <Icon size={11} strokeWidth={1.8} />
              {text}
            </button>
          ))}
        </div>
      </div>

      {err && <p className="font-data text-[11px] text-zinc-400">— {err}</p>}
      {!data && !err && (
        <p className="font-data text-[11px] text-zinc-600">
          Pick two different engagements to compare them.
        </p>
      )}

      {data && (
        <>
          <div className="grid grid-cols-2 gap-px bg-[var(--line)] sm:grid-cols-4 lg:grid-cols-7">
            {[
              ["Shared nodes", sum.shared_nodes, "both carry salience"],
              ["A only", sum.a_only_nodes, label(data.a)],
              ["B only", sum.b_only_nodes, label(data.b)],
              ["Shared edges", sum.shared_edges, "influence in both"],
              ["A only edges", sum.a_only_edges, label(data.a)],
              ["B only edges", sum.b_only_edges, label(data.b)],
              ["Mean |Δ|", sum.mean_abs_delta, `${sum.valence_conflicts} valence conflicts`],
            ].map(([k, v, s]) => (
              <div key={k} className="border border-[var(--line)] px-4 py-3">
                <p className="label">{k}</p>
                <p className="mt-1.5 font-data text-lg tabular-nums leading-none text-zinc-100">
                  {v}
                </p>
                <p className="mt-1.5 truncate font-data text-[10px] text-zinc-600">{s}</p>
              </div>
            ))}
          </div>

          {mode === "side" ? (
            <div className="grid gap-4 lg:grid-cols-2" data-testid="compare-side-by-side">
              {[data.a, data.b].map((s, i) => (
                <div key={s.subject} className="relative h-80 border border-[var(--line)]">
                  <MaslowSphere map={s.map} heat={s.heat || {}} showCandidates={false} />
                  <div className="panel pointer-events-none absolute left-4 top-4 px-3 py-2">
                    <p className="label">{i === 0 ? "A" : "B"}</p>
                    <p className="mt-1 text-xs text-zinc-100">{label(s)}</p>
                    <p className="mt-1 font-data text-[10px] text-zinc-600">
                      C {s.metrics.predictive_confidence}% · {s.coverage.filled}/
                      {s.coverage.total ?? 46} · {s.turns} turns
                    </p>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="relative h-[28rem] border border-[var(--line)]" data-testid="compare-merged">
              <MaslowSphere map={merged} compare={compareSides} showCandidates={false} />
              <div className="panel pointer-events-none absolute left-4 top-4 px-4 py-3">
                <p className="label">Merged sphere</p>
                <ul className="mt-2 space-y-1 font-data text-[10px] text-zinc-500">
                  <li>solid bright — shared by both</li>
                  <li>solid dim — {label(data.a)} only</li>
                  <li>wireframe — {label(data.b)} only</li>
                  <li>dashed edge — influence only in B</li>
                </ul>
              </div>
            </div>
          )}

          <div className="grid gap-10 lg:grid-cols-2">
            <div>
              <p className="label">Most divergent topics</p>
              <ul className="mt-3" data-testid="compare-divergent-nodes">
                {data.diff.divergent_nodes.map((n) => (
                  <li
                    key={n.id}
                    className="grid grid-cols-[1fr_auto] items-baseline gap-3 border-b border-[var(--line)] py-2.5"
                  >
                    <div className="min-w-0">
                      <p className="truncate text-xs text-zinc-200">
                        T{n.tier} {n.name}
                        {n.valence_conflict && (
                          <span className="ml-2 font-data text-[10px] text-zinc-100">
                            VALENCE CLASH
                          </span>
                        )}
                      </p>
                      <p className="mt-1 font-data text-[10px] text-zinc-600">
                        A {n.a_salience} ({n.a_valence}) · B {n.b_salience} ({n.b_valence})
                      </p>
                    </div>
                    <span className="font-data text-[11px] tabular-nums text-zinc-300">
                      {n.delta > 0 ? "+" : ""}
                      {n.delta}
                    </span>
                  </li>
                ))}
                {data.diff.divergent_nodes.length === 0 && (
                  <p className="font-data text-[11px] text-zinc-600">
                    Neither sphere carries salience yet.
                  </p>
                )}
              </ul>
            </div>

            <div>
              <p className="label">Influence node differences</p>
              <ul className="mt-3" data-testid="compare-edge-diff">
                {data.diff.edges.slice(0, 12).map((e) => (
                  <li key={e.id} className="border-b border-[var(--line)] py-2.5">
                    <p className="font-data text-[10px] uppercase tracking-[0.16em] text-zinc-500">
                      {e.side === "shared" ? "both" : e.side === "a" ? "A only" : "B only"} · T
                      {e.high_tier}→T{e.low_tier}
                      {e.valence_conflict ? " · OPPOSITE VALENCE" : ""}
                    </p>
                    <p className="mt-1 text-xs text-zinc-200">
                      {e.high_name} ⟶ {e.low_name}
                    </p>
                    <p className="mt-1 font-data text-[10px] text-zinc-600">
                      A {e.a_strength ?? "—"} {e.a_valence ? `(${e.a_valence})` : ""} · B{" "}
                      {e.b_strength ?? "—"} {e.b_valence ? `(${e.b_valence})` : ""} · Δ {e.delta}
                    </p>
                  </li>
                ))}
                {data.diff.edges.length === 0 && (
                  <p className="font-data text-[11px] text-zinc-600">
                    No promoted influence nodes in either engagement yet.
                  </p>
                )}
              </ul>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default EngagementCompare;
